import { useMemo } from 'react'
import useBedbanksRates from './useBedbanksRates'
import type { RoomDetails } from 'lib/search/searchUtils'
import getObjectKey from 'lib/object/getObjectKey'

interface BedbankRoom {
  rateId: string
  propertyId: string
  occupancy: App.Occupants
}

interface BedbankRoomBreakdownResult {
  rooms: Array<RoomDetails>
  isLoading: boolean
}

function useBedbankRoomBreakdown(
  bedbankRooms: Array<BedbankRoom>,
  filters?: App.OfferListFilters,
): BedbankRoomBreakdownResult {
  const bedbankRoomsKey = getObjectKey({ bedbankRooms })

  const occupancyByPropertyId = useMemo(() => {
    const map: Record<string, Array<App.Occupants>> = {}
    bedbankRooms.forEach(r => {
      if (!map[r.propertyId]) {
        map[r.propertyId] = []
      }
      map[r.propertyId].push(r.occupancy)
    })
    return map
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [bedbankRoomsKey])

  const [ratesMap, fetchingMap] = useBedbanksRates(
    occupancyByPropertyId,
    filters?.checkIn,
    filters?.checkOut,
    bedbankRooms.length > 0,
  )

  const bedbankRoomsList = useMemo((): Array<RoomDetails> => {
    // Group bedbank rooms by room type and count
    const roomDetails = bedbankRooms.reduce((acc, bedbankRoom) => {
      const isLoading = fetchingMap[bedbankRoom.propertyId]
      if (isLoading) return acc

      const propertyRates = ratesMap[bedbankRoom.propertyId]
      // rateId format: supplierCode|roomId|boardCode|...|... — extract the roomId to match across suppliers
      const bedbankRoomId = bedbankRoom.rateId.split('|')[1]
      const matchingRate = propertyRates?.hotelOnlyRates.find(rate =>
        rate.roomId === bedbankRoomId,
      )

      if (!matchingRate) return acc

      const roomTypeKey = `${bedbankRoom.propertyId}-${matchingRate.roomId}`

      if (!acc[roomTypeKey]) {
        acc[roomTypeKey] = { rate: matchingRate, count: 0 }
      }
      acc[roomTypeKey].count++

      return acc
    }, {} as Record<string, { rate: App.BedbankRate, count: number }>)

    // Transform grouped rooms into display format
    return Object.values(roomDetails)
      .map(({ rate, count }) => ({
        name: `${count}x ${rate.room.name}`,
        size: rate.room.features.find(f => f.id === 'dimension')?.text,
        bedConfiguration: rate.room.bedGroupsDescription,
        totalGuests: rate.room.capacities?.combinations?.length
          ? Math.max(...rate.room.capacities.combinations.map(c => c.adults + c.children + c.teenagers))
          : undefined,
        numberOfBedrooms: rate.room.numberOfBedrooms,
        numberOfBathrooms: rate.room.numberOfBathrooms,
      }))
  }, [bedbankRooms, ratesMap, fetchingMap])

  const isLoading = bedbankRooms.length > 0 && Object.values(fetchingMap).some(Boolean)

  return { rooms: bedbankRoomsList, isLoading }
}

export default useBedbankRoomBreakdown
